import React from "react";

import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import MenuIcon from "@mui/icons-material/Menu";

import { useSignOut, actionUpgradePlan } from "misc/account_utils";
import { getLanguageString as _ } from "misc/lang";
import css from "./profile.module.scss";
import cssTopMenu from "./topmenu.module.scss";
import {
  PageDescriptor,
  Pages,
  PlanEnterprise,
  PlansMultiUser,
} from "appConfig";
import { DefaultAndAuthAndOnelinkAndSubscriptionProps } from "model/props";

const Profile: React.FC<DefaultAndAuthAndOnelinkAndSubscriptionProps> = (
  props
) => {
  const { user, account, navigate, currentPlanCancelled, isSmallScreen } =
    props;

  const [anchorElem, setAnchorElem] = React.useState<HTMLElement | undefined>(
    undefined
  );

  const signOut = useSignOut(props);

  if (!user || !account) return <></>;

  const menuOpen = !!anchorElem;

  const userName =
    user.givenName && user.familyName
      ? user.givenName + " " + user.familyName
      : "";
  const accountName = account.info?.name || "";

  const gotoView = (page: PageDescriptor) => {
    navigate(page);
    setAnchorElem(undefined);
  };

  const _actionUpgradePlan = () => {
    setAnchorElem(undefined);
    actionUpgradePlan(props);
  };

  return (
    <>
      {isSmallScreen ? (
        <IconButton
          className={cssTopMenu.topMenuButtonProfile}
          component="span"
          onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
            setAnchorElem(event.currentTarget)
          }
          color="primary"
        >
          <input hidden accept="image/*" type="file" />
          {menuOpen ? <ExpandLessIcon /> : <MenuIcon />}
        </IconButton>
      ) : (
        <Button
          className={cssTopMenu.topMenuButtonProfile}
          component="span"
          onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
            setAnchorElem(event.currentTarget)
          }
          title={user.emailAddress || user.emailAddressPending}
          startIcon={!isSmallScreen && <AccountCircleIcon />}
          endIcon={menuOpen ? <ExpandLessIcon /> : <MenuIcon />}
          variant={isSmallScreen ? "text" : "outlined"}
          color="primary"
          size="small"
        >
          {!isSmallScreen &&
            (userName || user.emailAddress || user.emailAddressPending)}
        </Button>
      )}

      <Menu
        id="nonprofit-menu"
        anchorEl={anchorElem}
        open={menuOpen}
        onClose={() => setAnchorElem(undefined)}
        PaperProps={{
          style: {
            marginTop: 10,
            boxShadow: "0px 7px 12px 5px rgb(0 0 0 / 15%)",
          },
          elevation: 1,
        }}
        MenuListProps={{
          "aria-labelledby": "nonprofit-button",
          style: {
            textAlign: "center",
          },
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem divider={true}>
          <div className={css.info}>
            {userName && <div className={css.username}>{userName}</div>}
            <div className={css.email}>
              {user.emailAddress || user.emailAddressPending}
            </div>
            {accountName && <div className={css.secondary}>{accountName}</div>}
            {account.currentPlan && (
              <>
                <div className={css.plan}>
                  {_("general", account.currentPlan + "_plan")}
                </div>
                {currentPlanCancelled && (
                  <div className={css.cancelled}>
                    {_("topmenu", "subscription_cancelled")}
                  </div>
                )}
              </>
            )}
          </div>
        </MenuItem>

        <MenuItem onClick={() => gotoView(Pages.ACCOUNT)} divider={true}>
          {_("topmenu", "button_account_settings")}
        </MenuItem>

        {account.currentPlan !== PlanEnterprise && (
          <MenuItem onClick={_actionUpgradePlan} divider={true}>
            {_("topmenu", "button_upgrade_plan")}
          </MenuItem>
        )}

        {PlansMultiUser.includes(account.currentPlan as string) && (
          <MenuItem
            onClick={() => gotoView(Pages.MANAGE_ACCESS)}
            divider={true}
          >
            {_("topmenu", "button_manage_access")}
          </MenuItem>
        )}

        <MenuItem onClick={() => gotoView(Pages.USER_SETTINGS)} divider={true}>
          {_("topmenu", "button_user_settings")}
        </MenuItem>

        <MenuItem onClick={signOut}>{_("topmenu", "button_sign_out")}</MenuItem>
      </Menu>
    </>
  );
};

export default Profile;
